import "./i18n";
import { Suspense, lazy as _reactLazy, useEffect } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { LanguageProvider } from "@/providers/LanguageProvider";
import { RequireRole } from "@/components/auth/RequireRole";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RequireAiMentor } from "@/components/auth/RequireAiMentor";
import { RequireAdminMfa } from "@/components/auth/RequireAdminMfa";
import Landing from "./pages/Landing.tsx";
const Catalog = lazy(() => import("./pages/Catalog.tsx"));
const CatalogDetail = lazy(() => import("./pages/CatalogDetail.tsx"));
import TrackPage from "./pages/TrackPage.tsx";
import Pricing from "./pages/Pricing.tsx";
const ExpertsPricing = lazy(() => import("./pages/experts/Pricing.tsx"));
import NotFound from "./pages/NotFound.tsx";
import ServerError from "./pages/errors/ServerError.tsx";
import Login from "./pages/Login.tsx";
import Signup from "./pages/Signup.tsx";
import ForgotPassword from "./pages/ForgotPassword.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import OnboardingPersona from "./pages/OnboardingPersona.tsx";
import OnboardingTracks from "./pages/OnboardingTracks.tsx";
import OnboardingComplete from "./pages/OnboardingComplete.tsx";
import OnboardingRole from "./pages/onboarding/Role.tsx";
const Diagnostic = lazy(() => import("./pages/Diagnostic.tsx"));
import Careers from "./pages/Careers.jsx";
import CareerDetail from "./pages/CareerDetail.jsx";
import About from "./pages/About.tsx";
import Enterprise from "./pages/Enterprise.tsx";
import Teams from "./pages/Teams.tsx";
import Faq from "./pages/Faq.tsx";
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
import Contact from "./pages/Contact.tsx";
import { useLocation } from "react-router-dom";
import { CookieConsent } from "@/components/CookieConsent";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GeoBootstrap } from "@/components/GeoBootstrap";
import { VoiceBeacon } from "@/components/voice/VoiceBeacon";
import { LocaleGate } from "@/components/routing/LocaleGate";
import { LocaleRedirect } from "@/components/routing/LocaleRedirect";
import { RouteHead } from "@/components/seo/RouteHead";
import { pageview } from "@/lib/analytics";
import { ALL_ALIASES } from "@/i18n/route-aliases";
import { PageSkeleton } from "@/components/PageSkeleton";
import {
  BootcampGridSkeleton,
  MentorsGridSkeleton,
  MentorDetailSkeleton,
  BlogPageSkeleton,
  CommunityFeedSkeleton,
  CaseStudiesGridSkeleton,
  CaseStudyDetailSkeleton,
} from "@/components/skeletons/PublicSkeletons";

// Learn (authenticated, lazy)
const LearnerDashboard = lazy(() => import("./pages/learn/Dashboard.tsx"));
const MyBootcamps = lazy(() => import("./pages/learn/MyBootcamps.tsx"));
const BootcampProgress = lazy(() => import("./pages/learn/BootcampProgress.tsx"));
const LessonPlayer = lazy(() => import("./pages/learn/LessonPlayer.tsx"));
const Validator = lazy(() => import("./pages/learn/Validator.tsx"));
const ValidatorHub = lazy(() => import("./pages/learn/ValidatorHub.tsx"));
const BusinessValidator = lazy(() => import("./pages/BusinessValidator.tsx"));
const BusinessValidatorUnlock = lazy(() => import("./pages/BusinessValidatorUnlock.tsx"));
const Mentor = lazy(() => import("./pages/learn/Mentor.tsx"));
const MentorIndex = lazy(() => import("./pages/mentor/MentorIndex.tsx"));
const MentorConversationPage = lazy(() => import("./pages/mentor/MentorConversation.tsx"));
const MentorOnboarding = lazy(() => import("./pages/mentor/Onboarding.tsx"));
const MentorKnowledgeSources = lazy(() => import("./pages/mentor/KnowledgeSources.tsx"));
const VoiceAgentKnowledgeSources = lazy(() => import("./pages/voice-agent/KnowledgeSources.tsx"));
const MentoringHub = lazy(() => import("./pages/mentoring/Hub.tsx"));
// /learn/community* now redirects to /community (Founder Graph) — Phase A
// const AppCommunity = lazy(() => import("./pages/learn/Community.tsx"));
// const CommunityPost = lazy(() => import("./pages/learn/CommunityPost.tsx"));
const Certificates = lazy(() => import("./pages/learn/Certificates.tsx"));
const Resources = lazy(() => import("./pages/learn/Resources.tsx"));
const Outcomes = lazy(() => import("./pages/learn/Outcomes.tsx"));
const CoursePlayer = lazy(() => import("./pages/learn/CoursePlayer.tsx"));

// Community (lazy)
// LEGACY community (community_posts/replies). Kept lazy for the 30-day
// 301-redirect window — direct user nav goes to Founder Graph instead.
const Community = lazy(() => import("./pages/community/Index.tsx"));
const CommunityNewPost = lazy(() => import("./pages/community/NewPost.tsx"));
const CommunityPostDetail = lazy(() => import("./pages/community/PostDetail.tsx"));
// Founder Graph (replaces /community)
import "./lib/i18n"; // registers EN/FR community namespace bundles
const CommunityHome = lazy(() => import("./pages/CommunityHome.tsx").then(m => ({ default: m.CommunityHome })));
const CommunityLanding = lazy(() => import("./pages/CommunityLanding.tsx"));
import { CommunityEntry } from "./components/community/CommunityEntry";
const CommunitySpace = lazy(() => import("./pages/CommunitySpace.tsx").then(m => ({ default: m.CommunitySpace })));
const CommunityProfile = lazy(() => import("./pages/CommunityProfile.tsx").then(m => ({ default: m.CommunityProfile })));
const ModerationDashboard = lazy(() => import("./pages/ModerationDashboard.tsx").then(m => ({ default: m.ModerationDashboard })));
const CommunityMembers = lazy(() => import("./pages/community/Members.tsx"));
const CommunityEvents = lazy(() => import("./pages/community/Events.tsx"));
const CommunityEventDetail = lazy(() => import("./pages/community/EventDetail.tsx"));
const CommunityNewEvent = lazy(() => import("./pages/community/NewEvent.tsx"));
const CommunityAsk = lazy(() => import("./pages/community/Ask.tsx"));
const CommunityMessages = lazy(() => import("./pages/community/Messages.tsx"));
const CommunityMatches = lazy(() => import("./pages/community/Matches.tsx"));
const CommunityBrief = lazy(() => import("./pages/community/Brief.tsx"));
const CommunityLeaderboard = lazy(() => import("./pages/community/Leaderboard.tsx"));
const CommunityRooms = lazy(() => import("./pages/community/Rooms.tsx"));
const CommunityRoomDetail = lazy(() => import("./pages/community/RoomDetail.tsx"));
const CommunityNewRoom = lazy(() => import("./pages/community/NewRoom.tsx"));
const CommunityNewPoll = lazy(() => import("./pages/community/NewPoll.tsx"));
const CommunityWorkflows = lazy(() => import("./pages/community/Workflows.tsx"));
const CommunityNewWorkflow = lazy(() => import("./pages/community/NewWorkflow.tsx"));
const CommunityWorkflowDetail = lazy(() => import("./pages/community/WorkflowDetail.tsx"));
const CommunityAgents = lazy(() => import("./pages/community/Agents.tsx"));
const CommunityNewAgent = lazy(() => import("./pages/community/NewAgent.tsx"));
const CommunityAgentChat = lazy(() => import("./pages/community/AgentChat.tsx"));
const CommunityAiInbox = lazy(() => import("./pages/community/AiInbox.tsx"));
const MessagesPage = lazy(() => import("./pages/Messages.tsx"));
const PublicProfile = lazy(() => import("./pages/PublicProfile.tsx"));

// Services (most are lazy; landing pages eager for SEO)
import ServicesIndex from "./pages/services/Index.tsx";
import Microservices from "./pages/services/Microservices.tsx";
import Coaching from "./pages/services/Coaching.tsx";
const ServicesHub = lazy(() => import("./pages/services/Hub.tsx"));
const CoachRouter = lazy(() => import("./pages/services/CoachRouter.tsx"));
const MicroserviceDetail = lazy(() => import("./pages/services/MicroserviceDetail.tsx"));
const FractionalDetail = lazy(() => import("./pages/pillars/FractionalDetail.tsx"));
const BookingReceived = lazy(() => import("./pages/BookingReceived.tsx"));
const Retainers = lazy(() => import("./pages/services/Retainers.tsx"));
const InvestorReadiness = lazy(() => import("./pages/services/InvestorReadiness.tsx"));

// Velyra CEO — AI coach (lazy)
const CoachIndex = lazy(() => import("./pages/coach/CoachIndex.tsx"));
const CoachConversation = lazy(() => import("./pages/coach/CoachConversation.tsx"));
const VelyraActionsList = lazy(() => import("./pages/coach/ActionsList.tsx"));
const VelyraActionDetail = lazy(() => import("./pages/coach/ActionDetail.tsx"));
const VelyraKnowledge = lazy(() => import("./pages/coach/KnowledgeSources.tsx"));

// Settings (lazy)
const SettingsProfile = lazy(() => import("./pages/settings/Profile.tsx"));
const SettingsSecurity = lazy(() => import("./pages/settings/Security.tsx"));
const SettingsBilling = lazy(() => import("./pages/settings/Billing.tsx"));
const SettingsNotifications = lazy(() => import("./pages/settings/NotificationsSettings.tsx"));
const SettingsPreferences = lazy(() => import("./pages/settings/Preferences.tsx"));
const SettingsPrivacy = lazy(() => import("./pages/settings/Privacy.tsx"));
const NotificationsPage = lazy(() => import("./pages/Notifications.tsx"));
const CertVerify = lazy(() => import("./pages/CertVerify.tsx"));
const SearchPage = lazy(() => import("./pages/Search.tsx"));

// Admin (all lazy — admins are a small audience)
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard.tsx"));
const AdminArticlesList = lazy(() => import("./pages/admin/ArticlesList.tsx"));
const AdminArticleEditor = lazy(() => import("./pages/admin/ArticleEditor.tsx"));
const AdminProvidersList = lazy(() => import("./pages/admin/ProvidersList.tsx"));
// Provider Trust Engine (ptx_*)
const PtxAdminApplicationsList = lazy(() => import("./pages/admin/providers/ProviderApplicationsList.tsx"));
const PtxAdminApplicationDetail = lazy(() => import("./pages/admin/providers/ProviderApplicationDetail.tsx"));
const PtxAdminAppealsQueue = lazy(() => import("./pages/admin/providers/ProviderAppealsQueue.tsx"));
const PtxAdminComplaintsQueue = lazy(() => import("./pages/admin/providers/ProviderComplaintsQueue.tsx"));
const PtxAdminReverificationQueue = lazy(() => import("./pages/admin/providers/ProviderReverificationQueue.tsx"));
const PtxAdminListings = lazy(() => import("./pages/admin/providers/ProviderListingsAdmin.tsx"));
const PtxProvidersDirectory = lazy(() => import("./pages/providers/ProvidersDirectory.tsx"));
const PtxProviderPublicProfile = lazy(() => import("./pages/providers/ProviderPublicProfile.tsx"));
const AdminBootcampsList = lazy(() => import("./pages/admin/BootcampsList.tsx"));
const AdminServicesList = lazy(() => import("./pages/admin/ServicesList.tsx"));
const AdminMediaLibrary = lazy(() => import("./pages/admin/MediaLibrary.tsx"));
const AdminDownloadsList = lazy(() => import("./pages/admin/DownloadsList.tsx"));
const AdminNewsletterSubscribers = lazy(() => import("./pages/admin/newsletter/Subscribers.tsx"));
const AdminNewsletterLists = lazy(() => import("./pages/admin/newsletter/Lists.tsx"));
const AdminEmailSuppression = lazy(() => import("./pages/admin/newsletter/Suppression.tsx"));
const AdminNewsletterSettings = lazy(() => import("./pages/admin/newsletter/Settings.tsx"));
const AdminCampaigns = lazy(() => import("./pages/admin/newsletter/Campaigns.tsx"));
const AdminCampaignEditor = lazy(() => import("./pages/admin/newsletter/CampaignEditor.tsx"));
const AdminNewsletterLeads = lazy(() => import("./pages/admin/newsletter/Leads.tsx"));
const NewsletterPreferences = lazy(() => import("./pages/newsletter/Preferences.tsx"));
const AdminRetainersList = lazy(() => import("./pages/admin/RetainersList.tsx"));
const AdminCommunityModeration = lazy(() => import("./pages/admin/CommunityModeration.tsx"));
const AdminCommunityRouting = lazy(() => import("./pages/admin/CommunityRouting.tsx"));
const CommunityStartHere = lazy(() => import("./pages/community/StartHere.tsx"));
const AdminDisputes = lazy(() => import("./pages/admin/Disputes.tsx"));
const AdminSupportTriage = lazy(() => import("./pages/admin/SupportTriage.tsx"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics.tsx"));
const DesignSystem = lazy(() => import("./pages/DesignSystem.tsx"));

// Apply (lazy)
const ApplyLanding = lazy(() => import("./pages/apply/Landing.tsx"));
const ApplyForm = lazy(() => import("./pages/apply/Form.tsx"));

// DEV-only playground (tree-shaken in production by import.meta.env.DEV gate below).
const DevBadges = lazy(() => import("./pages/dev/Badges.tsx"));

// Provider portal (all lazy)
const ProviderDashboard = lazy(() => import("./pages/provider/Dashboard.tsx"));
const ProviderProfile = lazy(() => import("./pages/provider/Profile.tsx"));
const ProviderAvailability = lazy(() => import("./pages/provider/Availability.tsx"));
const ProviderEarnings = lazy(() => import("./pages/provider/Earnings.tsx"));
const ProviderDownloads = lazy(() => import("./pages/provider/Downloads.tsx"));
const ProviderKyc = lazy(() => import("./pages/provider/Kyc.tsx"));
const ProviderBookings = lazy(() => import("./pages/provider/Bookings.tsx"));
const ProviderDisputes = lazy(() => import("./pages/provider/Disputes.tsx"));
const ProviderServices = lazy(() => import("./pages/provider/Services.tsx"));
const MentorPayouts = lazy(() => import("./pages/mentor/Payouts.tsx"));

// Provider Profile Engine (lazy)
const ProviderEngineIntake = lazy(() => import("./components/provider-engine/ProviderIntake.tsx"));
const ProviderEngineReview = lazy(() => import("./components/provider-engine/ProviderProfileReview.tsx"));
const AdminProfileEngineConsole = lazy(() => import("./components/provider-engine/AdminProfileConsole.tsx"));
const ProviderMarketplace = lazy(() => import("./components/provider-engine/Marketplace.tsx"));
const MarketplaceMyBookings = lazy(() => import("./pages/marketplace/MyBookings.tsx"));

// Downloads marketplace (eager landing for SEO; checkout lazy)
import DownloadsIndex from "./pages/downloads/Index.tsx";
import DownloadsDetail from "./pages/downloads/Detail.tsx";
const DownloadsCheckout = lazy(() => import("./pages/downloads/Checkout.tsx"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess.tsx"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel.tsx"));
const CheckoutRedirect = lazy(() => import("./pages/CheckoutRedirect.tsx"));
const Library = lazy(() => import("./pages/Library.tsx"));
const UnifiedCheckout = lazy(() => import("./pages/Checkout.tsx"));
const AdminPayments = lazy(() => import("./pages/admin/Payments.tsx"));
const AdminRoleAudit = lazy(() => import("./pages/admin/RoleAudit.tsx"));
const AdminAuditTrail = lazy(() => import("./pages/admin/AuditTrail.tsx"));

// Support (lazy)
const AppSupport = lazy(() => import("./pages/Support.tsx"));
import SupportPublic from "./pages/SupportPublic.tsx";
const SupportThread = lazy(() => import("./pages/SupportThread.tsx"));

// Legal (lazy)
const Terms = lazy(() => import("./pages/legal/Terms.tsx"));
const Privacy = lazy(() => import("./pages/legal/Privacy.tsx"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy.tsx"));
const Dpa = lazy(() => import("./pages/legal/Dpa.tsx"));

// Pillars + case studies (eager — SEO landing)
import MentoringPillar from "./pages/pillars/Mentoring.tsx";
import FractionalPillar from "./pages/pillars/Fractional.tsx";
const CaseStudies = lazy(() => import("./pages/CaseStudies.tsx"));
const CaseStudyDetail = lazy(() => import("./pages/CaseStudyDetail.tsx"));

import AppRedirect from "./pages/AppRedirect.tsx";

const NewsletterConfirmed = lazy(() => import("./pages/NewsletterConfirmed.tsx"));
const NewsletterUnsubscribed = lazy(() => import("./pages/NewsletterUnsubscribed.tsx"));

// Mentor marketplace (public, lazy)
const MentorsIndex = lazy(() => import("./pages/mentors/Index.tsx"));
const MentorDetail = lazy(() => import("./pages/mentors/Detail.tsx"));
const CoachesIndex = lazy(() => import("./pages/coaches/Index.tsx"));

// Admin H4 (lazy)
const AdminMentorsList = lazy(() => import("./pages/admin/MentorsList.tsx"));
const AdminMentorEditor = lazy(() => import("./pages/admin/MentorEditor.tsx"));
const AdminCaseStudiesList = lazy(() => import("./pages/admin/CaseStudiesList.tsx"));
const AdminCaseStudyEditor = lazy(() => import("./pages/admin/CaseStudyEditor.tsx"));
const AdminVelyraTelemetry = lazy(() => import("./pages/admin/VelyraTelemetry.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5 * 60 * 1000, gcTime: 10 * 60 * 1000, retry: 1, refetchOnWindowFocus: false },
  },
});

function RouteSuspense({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSkeleton variant="marketing" />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

/** Per-route Suspense with a layout-matching fallback. */
function RS({ fallback, children }: { fallback: React.ReactNode; children: React.ReactNode }) {
  return <Suspense fallback={fallback}>{children}</Suspense>;
}

function AnalyticsTracker() {
  const loc = useLocation();
  useEffect(() => { pageview(loc.pathname); }, [loc.pathname]);
  useEffect(() => {
    // Successful mount → clear the chunk-reload guard so a future stale-chunk
    // event can self-heal again.
    try { sessionStorage.removeItem("bp.chunk-reload"); } catch { /* noop */ }
  }, []);
  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <GeoBootstrap />
              <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-3 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md">
                Skip to content
              </a>
              <AnalyticsTracker />
              <CookieConsent />
              <RouteSuspense>
              <Routes>
                {/* Locale-prefixed routes — all app paths live under /:locale/* */}
                <Route
                  path="/:locale/*"
                  element={
                    <LocaleGate>
                      <RouteHead />
                      <LocalizedRoutes />
                    </LocaleGate>
                  }
                />
                {/* Bare paths redirect to user's preferred locale */}
                <Route path="*" element={<LocaleRedirect />} />
              </Routes>
              </RouteSuspense>
              <VoiceBeacon />
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;

/** All application routes, mounted under /:locale/*. Paths are relative (no leading slash). */
function LocalizedRoutes() {
  return (
    <Routes>
                <Route path="" element={<Landing />} />
                <Route path="business-validator" element={<BusinessValidator />} />
                <Route path="validateur-business" element={<BusinessValidator />} />
                <Route path="business-validator/unlock" element={<BusinessValidatorUnlock />} />
                <Route path="validateur-business/deverrouiller" element={<BusinessValidatorUnlock />} />
                <Route path="dashboard" element={<RequireAuth><LearnerDashboard /></RequireAuth>} />
                <Route path="learn" element={<RequireAuth><MyBootcamps /></RequireAuth>} />
                <Route path="learn/courses/:slug" element={<RequireAuth><CoursePlayer /></RequireAuth>} />
                <Route path="learn/:enrollmentId/progress" element={<RequireAuth><BootcampProgress /></RequireAuth>} />
                <Route path="learn/:enrollmentId/lesson/:lessonId" element={<RequireAuth><LessonPlayer /></RequireAuth>} />
                <Route path="learn/validator" element={<RequireAuth><ValidatorHub /></RequireAuth>} />
                <Route path="learn/:enrollmentId/validator" element={<RequireAuth><Validator /></RequireAuth>} />
                {/* KORA AI Mentor — native Mentor surface */}
                <Route path="mentoring/hub" element={<RequireAuth><MentoringHub /></RequireAuth>} />
                <Route path="mentor" element={<RequireAuth><RequireAiMentor><MentorIndex /></RequireAiMentor></RequireAuth>} />
                <Route path="mentor/onboarding" element={<RequireAuth><RequireAiMentor><MentorOnboarding /></RequireAiMentor></RequireAuth>} />
                <Route path="mentor/sources" element={<RequireAuth><RequireAiMentor><MentorKnowledgeSources /></RequireAiMentor></RequireAuth>} />
                <Route path="mentor/:conversationId" element={<RequireAuth><RequireAiMentor><MentorConversationPage /></RequireAiMentor></RequireAuth>} />
                {/* Legacy persona-chat, rebranded as AI Strategist */}
                <Route path="strategist" element={<RequireAuth><RequireAiMentor><Mentor /></RequireAiMentor></RequireAuth>} />
                <Route path="strategist/:conversationId" element={<RequireAuth><RequireAiMentor><Mentor /></RequireAiMentor></RequireAuth>} />
                <Route path="outcomes" element={<RequireAuth><Outcomes /></RequireAuth>} />
                {/* Founder Graph — replaces legacy /community */}
                <Route
                  path="community"
                  element={
                    <CommunityEntry
                      member={<RequireAuth><RS fallback={<CommunityFeedSkeleton />}><CommunityHome /></RS></RequireAuth>}
                      landing={<RS fallback={<PageSkeleton />}><CommunityLanding /></RS>}
                    />
                  }
                />
                <Route path="community/start-here" element={<RequireAuth><RS fallback={<CommunityFeedSkeleton />}><CommunityStartHere /></RS></RequireAuth>} />
                <Route path="community/spaces/:slug" element={<RequireAuth><RS fallback={<CommunityFeedSkeleton />}><CommunitySpace /></RS></RequireAuth>} />
                <Route path="community/me" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityProfile /></RS></RequireAuth>} />
                <Route path="community/founders/:memberId" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityProfile /></RS></RequireAuth>} />
                <Route path="community/moderation" element={<RequireAdminMfa><RS fallback={<PageSkeleton />}><ModerationDashboard /></RS></RequireAdminMfa>} />
                <Route path="community/members" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityMembers /></RS></RequireAuth>} />
                <Route path="community/events" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityEvents /></RS></RequireAuth>} />
                <Route path="community/events/new" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityNewEvent /></RS></RequireAuth>} />
                <Route path="community/events/:id" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityEventDetail /></RS></RequireAuth>} />
                <Route path="community/ask" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityAsk /></RS></RequireAuth>} />
                <Route path="community/messages" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityMessages /></RS></RequireAuth>} />
                <Route path="community/messages/:threadId" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityMessages /></RS></RequireAuth>} />
                <Route path="community/matches" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityMatches /></RS></RequireAuth>} />
                <Route path="community/brief" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityBrief /></RS></RequireAuth>} />
                <Route path="community/leaderboard" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityLeaderboard /></RS></RequireAuth>} />
                <Route path="community/rooms" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityRooms /></RS></RequireAuth>} />
                <Route path="community/rooms/new" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityNewRoom /></RS></RequireAuth>} />
                <Route path="community/rooms/:roomId" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityRoomDetail /></RS></RequireAuth>} />
                <Route path="community/polls/new" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityNewPoll /></RS></RequireAuth>} />
                <Route path="community/workflows" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityWorkflows /></RS></RequireAuth>} />
                <Route path="community/workflows/new" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityNewWorkflow /></RS></RequireAuth>} />
                <Route path="community/workflows/:id" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityWorkflowDetail /></RS></RequireAuth>} />
                <Route path="community/agents" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityAgents /></RS></RequireAuth>} />
                <Route path="community/agents/new" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityNewAgent /></RS></RequireAuth>} />
                <Route path="community/agents/:agentId" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityAgentChat /></RS></RequireAuth>} />
                <Route path="community/ai-inbox" element={<RequireAuth><RS fallback={<PageSkeleton />}><CommunityAiInbox /></RS></RequireAuth>} />
                {/* Legacy 301s — 30-day window (remove ~2026-06-15) */}
                <Route path="community/new" element={<Navigate to="../community" replace />} />
                <Route path="community/legacy" element={<RS fallback={<CommunityFeedSkeleton />}><Community /></RS>} />
                <Route path="community/legacy/new" element={<RequireAuth><CommunityNewPost /></RequireAuth>} />
                <Route path="community/legacy/:postId" element={<RS fallback={<CommunityFeedSkeleton />}><CommunityPostDetail /></RS>} />
                <Route path="app/community" element={<Navigate to="../community" replace />} />
                <Route path="app/community/:postId" element={<Navigate to="../community" replace />} />
                <Route path="learn/community" element={<Navigate to="../community" replace />} />
                <Route path="learn/community/:postId" element={<Navigate to="../community" replace />} />
                <Route path="u/:userId" element={<PublicProfile />} />
                <Route path="messages" element={<RequireAuth><MessagesPage /></RequireAuth>} />
                <Route path="messages/:threadId" element={<RequireAuth><MessagesPage /></RequireAuth>} />
                <Route path="certificates" element={<RequireAuth><Certificates /></RequireAuth>} />
                <Route path="resources" element={<RequireAuth><Resources /></RequireAuth>} />
                <Route path="app" element={<AppRedirect />} />
                <Route path="services" element={<ServicesIndex />} />
                <Route path="services/hub" element={<RequireAuth><ServicesHub /></RequireAuth>} />
                <Route path="services/microservices" element={<Microservices />} />
                <Route path="services/coaching" element={<Coaching />} />
                <Route path="services/coaching/:providerId" element={<CoachRouter />} />
                <Route path="coach" element={<RequireAuth><RequireAiMentor><CoachIndex /></RequireAiMentor></RequireAuth>} />
                <Route path="coach/knowledge" element={<RequireAuth><RequireAiMentor><VelyraKnowledge /></RequireAiMentor></RequireAuth>} />
                <Route path="coach/:conversationId" element={<RequireAuth><RequireAiMentor><CoachConversation /></RequireAiMentor></RequireAuth>} />
                <Route path="actions" element={<RequireAuth><RequireAiMentor><VelyraActionsList /></RequireAiMentor></RequireAuth>} />
                <Route path="actions/:planId" element={<RequireAuth><RequireAiMentor><VelyraActionDetail /></RequireAiMentor></RequireAuth>} />
                <Route path="services/microservices/:slug" element={<MicroserviceDetail />} />
                <Route path="booking/received" element={<BookingReceived />} />
                <Route path="services/retainers" element={<Retainers />} />
                <Route path="services/investor-readiness" element={<InvestorReadiness />} />

                {/* Mentor marketplace (public) */}
                <Route path="mentors" element={<RS fallback={<MentorsGridSkeleton />}><MentorsIndex /></RS>} />
                <Route path="mentors/:slug" element={<RS fallback={<MentorDetailSkeleton />}><MentorDetail /></RS>} />
                <Route path="coaches" element={<RS fallback={<PageSkeleton />}><CoachesIndex type="coach" /></RS>} />

                {/* Settings */}
                <Route path="settings" element={<Navigate to="profile" replace />} />
                <Route path="settings/profile" element={<RequireAuth><SettingsProfile /></RequireAuth>} />
                <Route path="settings/security" element={<RequireAuth><SettingsSecurity /></RequireAuth>} />
                <Route path="settings/billing" element={<RequireAuth><SettingsBilling /></RequireAuth>} />
                <Route path="settings/notifications" element={<RequireAuth><SettingsNotifications /></RequireAuth>} />
                <Route path="settings/preferences" element={<RequireAuth><SettingsPreferences /></RequireAuth>} />
                <Route path="settings/privacy" element={<RequireAuth><SettingsPrivacy /></RequireAuth>} />

                {/* Notifications center */}
                <Route path="notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />

                {/* Public utility */}
                <Route path="cert/:code" element={<CertVerify />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="500" element={<ServerError />} />
                <Route path="newsletter/confirmed" element={<NewsletterConfirmed />} />
                <Route path="newsletter/unsubscribed" element={<NewsletterUnsubscribed />} />
                <Route path="newsletter/preferences" element={<NewsletterPreferences />} />

                <Route path="catalog" element={<RS fallback={<BootcampGridSkeleton />}><Catalog /></RS>} />
                <Route path="catalog/:slug" element={<RS fallback={<BootcampGridSkeleton />}><CatalogDetail /></RS>} />
                <Route path="tracks/:slug" element={<TrackPage />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="plans" element={<Navigate to="../pricing" replace />} />
                <Route path="experts/pricing" element={<RS fallback={<PageSkeleton />}><ExpertsPricing /></RS>} />
                <Route path="about" element={<About />} />
                <Route path="enterprise" element={<Enterprise />} />
                <Route path="teams" element={<Teams />} />
                <Route path="faq" element={<Faq />} />
                <Route path="diagnostic" element={<Diagnostic />} />
                {/* Pillar slug aliases — bare paths the audit surfaced as 404s */}
                <Route path="coaching" element={<Navigate to="../services/coaching" replace />} />
                <Route path="executives" element={<Navigate to="../fractional" replace />} />
                <Route path="microservices" element={<Navigate to="../services/microservices" replace />} />
                <Route path="blog" element={<RS fallback={<BlogPageSkeleton />}><Blog /></RS>} />
                <Route path="blog/:slug" element={<RS fallback={<BlogPageSkeleton />}><BlogPost /></RS>} />
                <Route path="contact" element={<Contact />} />
                {/* Legacy URLs from previous site — kept here to redirect Google's cached 404s
                    to current equivalents (see GSC "Not found (404)" report). */}
                <Route path="contact-us" element={<Navigate to="../contact" replace />} />
                <Route path="blogs" element={<Navigate to="../blog" replace />} />
                <Route path="blogs/*" element={<Navigate to="../blog" replace />} />
                <Route path="courses/Strategic-Planning-and-Execution" element={<Navigate to="../catalog" replace />} />
                <Route path="knowledge-base-topicks" element={<Navigate to="../faq" replace />} />
                <Route path="knowledge-base" element={<Navigate to="../faq" replace />} />
                <Route path="knowledge-base/*" element={<Navigate to="../faq" replace />} />
                <Route path="careers" element={<Careers />} />
                <Route path="careers/:slug" element={<CareerDetail />} />
                <Route path="login" element={<Login />} />
                <Route path="signup" element={<Signup />} />
                <Route path="auth" element={<Navigate to="../signup" replace />}/>
                <Route path="auth/forgot-password" element={<ForgotPassword />} />
                <Route path="auth/reset-password" element={<ResetPassword />} />
                <Route path="onboarding/persona" element={<RequireAuth requireOnboarding={false}><OnboardingPersona /></RequireAuth>} />
                <Route path="onboarding/tracks" element={<RequireAuth requireOnboarding={false}><OnboardingTracks /></RequireAuth>} />
                <Route path="onboarding/diagnostic" element={<RequireAuth requireOnboarding={false}><Diagnostic /></RequireAuth>} />
                <Route path="onboarding/complete" element={<RequireAuth requireOnboarding={false}><OnboardingComplete /></RequireAuth>} />
                <Route path="onboarding/role" element={<RequireAuth requireOnboarding={false}><OnboardingRole /></RequireAuth>} />
                <Route path="design-system" element={<RequireRole role="admin" onDeny="notFound"><DesignSystem /></RequireRole>} />
                {import.meta.env.DEV && (
                  <Route path="dev/badges" element={<DevBadges />} />
                )}

                {/* Admin CMS */}
                <Route path="admin" element={<RequireAdminMfa><AdminDashboard /></RequireAdminMfa>} />
                <Route path="admin/articles" element={<RequireAdminMfa><AdminArticlesList /></RequireAdminMfa>} />
                <Route path="admin/articles/new" element={<RequireAdminMfa><AdminArticleEditor /></RequireAdminMfa>} />
                <Route path="admin/articles/:id/edit" element={<RequireAdminMfa><AdminArticleEditor /></RequireAdminMfa>} />
                <Route path="admin/providers" element={<RequireAdminMfa><AdminProvidersList /></RequireAdminMfa>} />
                {/* Provider Trust Engine */}
                <Route path="admin/providers/applications" element={<RequireAdminMfa><PtxAdminApplicationsList /></RequireAdminMfa>} />
                <Route path="admin/providers/applications/:id" element={<RequireAdminMfa><PtxAdminApplicationDetail /></RequireAdminMfa>} />
                <Route path="admin/providers/appeals" element={<RequireAdminMfa><PtxAdminAppealsQueue /></RequireAdminMfa>} />
                <Route path="admin/providers/complaints" element={<RequireAdminMfa><PtxAdminComplaintsQueue /></RequireAdminMfa>} />
                <Route path="admin/providers/reverification" element={<RequireAdminMfa><PtxAdminReverificationQueue /></RequireAdminMfa>} />
                <Route path="admin/providers/listings" element={<RequireAdminMfa><PtxAdminListings /></RequireAdminMfa>} />
                <Route path="providers" element={<PtxProvidersDirectory />} />
                <Route path="providers/:id" element={<PtxProviderPublicProfile />} />
                <Route path="admin/bootcamps" element={<RequireAdminMfa><AdminBootcampsList /></RequireAdminMfa>} />
                <Route path="admin/services" element={<RequireAdminMfa><AdminServicesList /></RequireAdminMfa>} />
                <Route path="admin/media" element={<RequireAdminMfa><AdminMediaLibrary /></RequireAdminMfa>} />
                <Route path="admin/downloads" element={<RequireAdminMfa><AdminDownloadsList /></RequireAdminMfa>} />
                <Route path="admin/newsletter/subscribers" element={<RequireAdminMfa><AdminNewsletterSubscribers /></RequireAdminMfa>} />
                <Route path="admin/newsletter/lists" element={<RequireAdminMfa><AdminNewsletterLists /></RequireAdminMfa>} />
                <Route path="admin/newsletter/suppression" element={<RequireAdminMfa><AdminEmailSuppression /></RequireAdminMfa>} />
                <Route path="admin/newsletter/settings" element={<RequireAdminMfa><AdminNewsletterSettings /></RequireAdminMfa>} />
                <Route path="admin/newsletter/campaigns" element={<RequireAdminMfa><AdminCampaigns /></RequireAdminMfa>} />
                <Route path="admin/newsletter/campaigns/:id" element={<RequireAdminMfa><AdminCampaignEditor /></RequireAdminMfa>} />
                <Route path="admin/newsletter/leads" element={<RequireAdminMfa><AdminNewsletterLeads /></RequireAdminMfa>} />
                <Route path="admin/retainers" element={<RequireAdminMfa><AdminRetainersList /></RequireAdminMfa>} />
                <Route path="admin/community" element={<RequireAdminMfa><AdminCommunityModeration /></RequireAdminMfa>} />
                <Route path="admin/community/routing" element={<RequireAdminMfa><AdminCommunityRouting /></RequireAdminMfa>} />
                <Route path="admin/disputes" element={<RequireAdminMfa><AdminDisputes /></RequireAdminMfa>} />
                <Route path="admin/analytics" element={<RequireAdminMfa><AdminAnalytics /></RequireAdminMfa>} />
                <Route path="admin/mentors" element={<RequireAdminMfa><AdminMentorsList /></RequireAdminMfa>} />
                <Route path="admin/mentors/new" element={<RequireAdminMfa><AdminMentorEditor /></RequireAdminMfa>} />
                <Route path="admin/mentors/:id/edit" element={<RequireAdminMfa><AdminMentorEditor /></RequireAdminMfa>} />
                <Route path="admin/case-studies" element={<RequireAdminMfa><AdminCaseStudiesList /></RequireAdminMfa>} />
                <Route path="admin/case-studies/new" element={<RequireAdminMfa><AdminCaseStudyEditor /></RequireAdminMfa>} />
                <Route path="admin/case-studies/:id/edit" element={<RequireAdminMfa><AdminCaseStudyEditor /></RequireAdminMfa>} />
                <Route path="admin/payments" element={<RequireAdminMfa><AdminPayments /></RequireAdminMfa>} />
                <Route path="admin/role-audit" element={<RequireAdminMfa><AdminRoleAudit /></RequireAdminMfa>} />
                <Route path="admin/audit" element={<RequireAdminMfa><AdminAuditTrail /></RequireAdminMfa>} />
                <Route path="admin/velyra/telemetry" element={<RequireAdminMfa><AdminVelyraTelemetry /></RequireAdminMfa>} />
                <Route path="admin/voice-agent/knowledge" element={<RequireAdminMfa><VoiceAgentKnowledgeSources /></RequireAdminMfa>} />

                {/* Provider portal */}
                <Route path="apply" element={<ApplyLanding />} />
                <Route path="apply/form" element={<ApplyForm />} />
                <Route path="provider" element={<RequireRole role="provider"><ProviderDashboard /></RequireRole>} />
                <Route path="provider/profile" element={<RequireRole role="provider"><ProviderProfile /></RequireRole>} />
                <Route path="provider/availability" element={<RequireRole role="provider"><ProviderAvailability /></RequireRole>} />
                <Route path="provider/earnings" element={<RequireRole role="provider"><ProviderEarnings /></RequireRole>} />
                <Route path="provider/downloads" element={<RequireRole role="provider"><ProviderDownloads /></RequireRole>} />
                <Route path="provider/kyc" element={<RequireRole role="provider"><ProviderKyc /></RequireRole>} />
                <Route path="provider/bookings" element={<RequireRole role="provider"><ProviderBookings /></RequireRole>} />
                <Route path="provider/disputes" element={<RequireRole role="provider"><ProviderDisputes /></RequireRole>} />
                <Route path="provider/services" element={<RequireRole role="provider"><ProviderServices /></RequireRole>} />
                <Route path="mentor/payouts" element={<RequireRole role="provider"><MentorPayouts /></RequireRole>} />

                {/* Provider Profile Engine */}
                <Route path="provider/profile-engine/intake" element={<RequireRole role="provider"><ProviderEngineIntake /></RequireRole>} />
                <Route path="provider/profile-engine/review/:profileId" element={<RequireRole role="provider"><ProviderEngineReview /></RequireRole>} />
                <Route path="admin/profile-engine" element={<RequireAdminMfa><AdminProfileEngineConsole /></RequireAdminMfa>} />
                <Route path="marketplace" element={<ProviderMarketplace />} />
                <Route path="marketplace/bookings" element={<RequireAuth><MarketplaceMyBookings /></RequireAuth>} />

                {/* Downloads marketplace */}
                <Route path="downloads" element={<DownloadsIndex />} />
                <Route path="downloads/:slug" element={<DownloadsDetail />} />
                <Route path="downloads/:slug/checkout" element={<RequireAuth><DownloadsCheckout /></RequireAuth>} />
                <Route path="checkout/success" element={<RequireAuth><CheckoutSuccess /></RequireAuth>} />
                <Route path="checkout/cancel" element={<RequireAuth><CheckoutCancel /></RequireAuth>} />
                <Route path="checkout/redirect" element={<RequireAuth><CheckoutRedirect /></RequireAuth>} />
                <Route path="checkout/:sku" element={<RequireAuth><UnifiedCheckout /></RequireAuth>} />
                <Route path="library" element={<RequireAuth><Library /></RequireAuth>} />

                {/* Support */}
                <Route path="support" element={<SupportPublic />} />
                <Route path="app/support" element={<RequireAuth><AppSupport /></RequireAuth>} />
                <Route path="support/:ticketId" element={<RequireAuth><SupportThread /></RequireAuth>} />
                <Route path="admin/support" element={<RequireAdminMfa><AdminSupportTriage /></RequireAdminMfa>} />

                {/* Legal */}
                <Route path="legal/terms" element={<Terms />} />
                <Route path="legal/privacy" element={<Privacy />} />
                <Route path="legal/refund-policy" element={<RefundPolicy />} />
                <Route path="legal/dpa" element={<Dpa />} />

                {/* Ecosystem pillar landing pages */}
                <Route path="mentoring" element={<MentoringPillar />} />
                <Route path="fractional" element={<FractionalPillar />} />
                <Route path="fractional/:slug" element={<FractionalDetail />} />

                {/* Case studies */}
                <Route path="case-studies" element={<RS fallback={<CaseStudiesGridSkeleton />}><CaseStudies /></RS>} />
                <Route path="case-studies/:slug" element={<RS fallback={<CaseStudyDetailSkeleton />}><CaseStudyDetail /></RS>} />

                {/* Locale-aware aliases (additive — canonical EN paths above keep working) */}
                <Route path="bootcamps" element={<RS fallback={<BootcampGridSkeleton />}><Catalog /></RS>} />
                <Route path="bootcamps/:slug" element={<RS fallback={<BootcampGridSkeleton />}><CatalogDetail /></RS>} />
                <Route path="courses" element={<RS fallback={<BootcampGridSkeleton />}><Catalog /></RS>} />
                <Route path="courses/:slug" element={<RS fallback={<BootcampGridSkeleton />}><CatalogDetail /></RS>} />
                {ALL_ALIASES.map(({ canonical, alias }) => {
                  // skip catalog — handled above with /:slug variants
                  if (canonical === "catalog") return null;
                  const element = (() => {
                    switch (canonical) {
                      case "pricing": return <Pricing />;
                      case "case-studies": return <RS fallback={<CaseStudiesGridSkeleton />}><CaseStudies /></RS>;
                      case "downloads": return <DownloadsIndex />;
                      case "careers": return <Careers />;
                      case "about": return <About />;
                      case "login": return <Login />;
                      case "signup": return <Signup />;
                    }
                  })();
                  return <Route key={`${canonical}:${alias}`} path={alias} element={element} />;
                })}
                {/* case-studies detail aliases */}
                <Route path="etudes-de-cas/:slug" element={<RS fallback={<CaseStudyDetailSkeleton />}><CaseStudyDetail /></RS>} />
                <Route path="estudios-de-caso/:slug" element={<RS fallback={<CaseStudyDetailSkeleton />}><CaseStudyDetail /></RS>} />
                <Route path="estudos-de-caso/:slug" element={<RS fallback={<CaseStudyDetailSkeleton />}><CaseStudyDetail /></RS>} />
                {/* downloads detail aliases */}
                <Route path="telechargements/:slug" element={<DownloadsDetail />} />
                <Route path="descargas/:slug" element={<DownloadsDetail />} />
                {/* careers detail aliases */}
                <Route path="carrieres/:slug" element={<CareerDetail />} />
                <Route path="empleos/:slug" element={<CareerDetail />} />
                <Route path="carreiras/:slug" element={<CareerDetail />} />

                <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

